import { pageMainVariants } from '../../../../core/components/page-main'
import { Separator } from '../../../../core/components/Separator'
import { Tabs, TabsList, TabsTrigger } from '../../../../core/components/Tabs'
import { Tooltip } from '../../../../core/components/Tooltip'
import { useHasUserCapability } from '../../../hooks/useHasUserCapability'
import { EncounterDetailsPageHotkeys } from '../../../utils/hotkeys'
import { parseAsStringEnum, useQueryStates } from 'nuqs'
import { useHotkeys } from '@tanstack/react-hotkeys'
import {
  filterDefinitionsByCapability,
  cn,
  createEnumSchema,
} from '@joyful/utils'
import type { EnumValueMeta, UserCapability } from '@joyful/utils'
import { useEffect } from 'react'
import type { ReactNode } from 'react'

type EncounterTab =
  | 'overview'
  | 'details'
  | 'eligibility'
  | 'files'
  | 'case'
  | 'appeal_packets'
  | 'decision_path'

interface EncounterTabMeta extends EnumValueMeta {
  capability?: UserCapability
  hotkey?:
    | typeof EncounterDetailsPageHotkeys.overviewTab
    | typeof EncounterDetailsPageHotkeys.detailsTab
    | typeof EncounterDetailsPageHotkeys.eligibilityTab
}

const EncounterTabSchema = createEnumSchema<EncounterTab, EncounterTabMeta>([
  {
    hotkey: EncounterDetailsPageHotkeys.overviewTab,
    title: 'Overview',
    value: 'overview',
  },
  {
    hotkey: EncounterDetailsPageHotkeys.detailsTab,
    title: 'Details',
    value: 'details',
  },
  {
    hotkey: EncounterDetailsPageHotkeys.eligibilityTab,
    title: 'Eligibility',
    value: 'eligibility',
  },
  {
    capability: 'dropbox.linked_record.view',
    title: 'Files',
    value: 'files',
  },
  {
    title: 'Case',
    value: 'case',
  },
  {
    capability: 'outbound_packets.view',
    title: 'Appeal Packets',
    value: 'appeal_packets',
  },
  {
    capability: 'logic_diagram.view',
    title: 'Decision tree',
    value: 'decision_path',
  },
])

export function createEncounterDetailTabQueryMap(
  defaultTab: EncounterTab = 'overview',
) {
  return {
    tab: parseAsStringEnum(EncounterTabSchema.values()).withDefault(defaultTab),
  }
}

export const EncounterDetailTabQueryMap = createEncounterDetailTabQueryMap()

export function useEncounterDetailTab(defaultTab: EncounterTab = 'overview') {
  return useQueryStates(createEncounterDetailTabQueryMap(defaultTab))
}

export function EncounterTabs({
  children,
  defaultTab = 'overview',
  filesCountBadge,
  shouldShowAppealPacketsTab = true,
  shouldShowCaseTab = false,
  shouldShowDecisionPathTab = false,
  shouldShowFilesTab = true,
}: {
  children: ReactNode
  defaultTab?: EncounterTab
  filesCountBadge?: (isActive: boolean) => ReactNode
  shouldShowAppealPacketsTab?: boolean
  shouldShowCaseTab?: boolean
  shouldShowDecisionPathTab?: boolean
  shouldShowFilesTab?: boolean
}) {
  const [currentTab, setCurrentTab] = useEncounterDetailTab(defaultTab)
  const hasCapability = useHasUserCapability()
  const capabilityVisibleTabs = filterDefinitionsByCapability(
    EncounterTabSchema.definitions,
    hasCapability,
  )
  const visibleTabs = capabilityVisibleTabs.filter((tab) => {
    if (tab.value === 'files') {
      return shouldShowFilesTab
    }

    if (tab.value === 'case') {
      return shouldShowCaseTab
    }

    if (tab.value === 'appeal_packets') {
      return shouldShowAppealPacketsTab
    }

    if (tab.value === 'decision_path') {
      return shouldShowDecisionPathTab
    }

    return true
  })
  const activeTab = visibleTabs.some((tab) => {
    return tab.value === currentTab.tab
  })
    ? currentTab.tab
    : visibleTabs[0]?.value

  useEffect(() => {
    if (activeTab !== undefined && activeTab !== currentTab.tab) {
      void setCurrentTab({ tab: activeTab })
    }
  }, [activeTab, currentTab.tab, setCurrentTab])

  useHotkeys(
    visibleTabs.flatMap((tab) => {
      if (tab.hotkey === undefined) {
        return []
      }

      return [
        {
          callback: () => {
            void setCurrentTab({ tab: tab.value })
          },
          hotkey: tab.hotkey,
        },
      ]
    }),
  )

  return (
    <Tabs
      className="flex flex-col"
      value={activeTab ?? currentTab.tab}
      onValueChange={(value) => {
        void setCurrentTab({
          tab: EncounterTabSchema.schema.parse(value),
        })
      }}
    >
      <div className={pageMainVariants({ padding: 'horizontal' })}>
        <TabsList className="scrollable-x border-b-0">
          {visibleTabs.map((tab) => {
            return (
              <Tooltip
                key={tab.value}
                content={tab.title}
                disabled={tab.hotkey === undefined}
                hotkey={tab.hotkey}
                tooltipRootProps={{ delayDuration: 750 }}
              >
                <span className="inline-flex">
                  <TabsTrigger aria-keyshortcuts={tab.hotkey} value={tab.value}>
                    <span className="inline-flex items-center gap-x-1.5">
                      <span>{tab.title}</span>
                      {tab.value === 'files'
                        ? filesCountBadge?.(activeTab === 'files')
                        : null}
                    </span>
                  </TabsTrigger>
                </span>
              </Tooltip>
            )
          })}
        </TabsList>
      </div>
      <Separator />
      <div className={cn(pageMainVariants({ padding: 'horizontal' }), 'pt-5')}>
        {children}
      </div>
    </Tabs>
  )
}
